import { Injectable, Injector, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, exhaustMap, of, switchMap, tap, timer } from 'rxjs';
import { environment } from '@environments/environment';
import { HealthModel } from '@models/health/health.model';
import { ConnectionStatusService } from './connection-status.service';
import { HttpService } from './http.service';

export const HEALTH_URL = `${environment.caisseApiUrl}/health`;

const OFFLINE_CHECK_INTERVAL_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class ConnectionMonitorService {
  private readonly httpService = inject(HttpService);
  private readonly connectionStatus = inject(ConnectionStatusService);
  private readonly injector = inject(Injector);

  #started = false;

  start(): void {
    if (this.#started || environment.isFullOnline) {
      return;
    }
    this.#started = true;

    toObservable(this.connectionStatus.status, { injector: this.injector })
      .pipe(switchMap((status) => (status === 'common.offline' ? this.#pollUntilOnline() : EMPTY)))
      .subscribe();
  }

  #pollUntilOnline(): Observable<HealthModel | null> {
    return timer(0, OFFLINE_CHECK_INTERVAL_MS).pipe(exhaustMap(() => this.#check()));
  }

  #check(): Observable<HealthModel | null> {
    return this.httpService.get<HealthModel>(HEALTH_URL).pipe(
      tap(() => this.connectionStatus.setOnline()),
      catchError(() => of(null)),
    );
  }
}
